import { BODY_HEIGHT, BODY_RADIUS, GRAVITY, JUMP_SPEED, LEG_RADIUS, LEGS_TOP, MOVE_SUBSTEP, SNAP_DOWN, STEP_UP, circleHits, groundUnder, moveBody, slabPolygons } from "./body";

/** The local player between frames; y is the feet. */
export interface WalkState { x: number; y: number; z: number; vy: number; grounded: boolean }
/** One frame's wish: the horizontal move (speed × dt already applied) and whether jump is held. */
export interface WalkInput { dx: number; dz: number; jump: boolean }
/** What the walker needs from the world: collider triangles in a box (world space, 9 floats each) and the terrain. */
export interface WalkWorld {
  triangles(minX: number, minY: number, minZ: number, maxX: number, maxY: number, maxZ: number): Float32Array;
  terrain(x: number, z: number): number;
}

/**
 * The band starts just above STEP_UP, so a ledge exactly that tall is stepped onto rather than walked into;
 * the ground search reaches as far past STEP_UP and SNAP_DOWN, so Float32 triangles (0.2 is 0.2000000029)
 * do not turn a drop of exactly SNAP_DOWN, such as the library porch, into a fall.
 */
const BAND_GAP = 0.001;
/** A wall the body rests against is not a ceiling. */
const HEAD_CONTACT = 0.01;
/**
 * A slope counts as ground only where it does not lift the feet; this is the whole tolerance, so no heading, however
 * close to the slope's contour, creeps up it a hair a step. Standing still on a slope is bit-exact, so it can be tiny.
 */
const SLOPE_EPS = 1e-6;

/**
 * One frame of walking, jumping and falling (spec 2m §3.2). The body has two bands: the legs (STEP_UP..LEGS_TOP,
 * LEG_RADIUS) and the body above (LEGS_TOP..BODY_HEIGHT, BODY_RADIUS); the head room checks use BODY_RADIUS, since
 * the head is in the upper band (spec 2m-2 §4.2).
 */
export function walkStep(s: WalkState, input: WalkInput, dt: number, world: WalkWorld): WalkState {
  let { x, y, z, vy, grounded } = s;
  const r = BODY_RADIUS;
  // 1. vertical speed, integrated exactly for constant gravity so the jump apex does not depend on the frame rate
  if (grounded && input.jump) { vy = JUMP_SPEED; grounded = false; }
  let dy = 0;
  if (grounded) vy = 0;
  else { dy = vy * dt - 0.5 * GRAVITY * dt * dt; vy -= GRAVITY * dt; }
  const reach = Math.hypot(input.dx, input.dz) + r + 0.1;
  const steps = Math.max(1, Math.ceil(Math.hypot(input.dx, input.dz) / MOVE_SUBSTEP));
  // every substep may step up once, and the headroom over the last one must be in what we fetched
  const rise = Math.max(dy, 0) + steps * (STEP_UP + BAND_GAP);
  const tris = world.triangles(x - reach, y + Math.min(dy, 0) - SNAP_DOWN, z - reach, x + reach, y + rise + BODY_HEIGHT, z + reach);
  // 2. the head meets a ceiling
  if (dy > 0) {
    const head = slabPolygons(tris, y + BODY_HEIGHT, y + BODY_HEIGHT + dy, { minX: x - r, minZ: z - r, maxX: x + r, maxZ: z + r });
    if (circleHits(head, x, z, r - HEAD_CONTACT)) { dy = 0; vy = 0; }
  }
  // 3. vertical move, then the ground: a fast fall lands before moving sideways, so a floor it fell into is
  // never inside the body's band (the horizontal pushes would throw the body off it)
  const startY = y;
  y += dy;
  // 5. the ground: a face steeper than the ramp limit counts only where it does not lift the feet above liftFrom
  // (spec 2m-2 §4.2), so walking down it or landing on it still works; true when such a face lies above the feet
  // and above the ground chosen, which means the centre has walked into a slope. Checked while rising too: a jump
  // at a slope must not carry the centre under its surface, where the landing would fall through it
  const settle = (liftFrom: number): boolean => {
    const bottom = y - (grounded ? SNAP_DOWN + BAND_GAP : 0), top = Math.max(startY, y) + STEP_UP + BAND_GAP;
    let ground = -Infinity;
    if (vy > 0) grounded = false;
    else {
      ground = Math.max(groundUnder(tris, x, z, bottom, top, liftFrom + SLOPE_EPS) ?? -Infinity, world.terrain(x, z));
      if (ground >= bottom && ground <= top) { y = ground; vy = 0; grounded = true; }
      else grounded = false;
    }
    return (groundUnder(tris, x, z, bottom, top) ?? -Infinity) > Math.max(ground, liftFrom) + SLOPE_EPS;
  };
  settle(startY);
  // the band clears what the feet were under; raising them to `to` also needs the slab above the head clear
  const roomless = (from: number, to: number): boolean => to > from + 1e-9
    && circleHits(slabPolygons(tris, from + BODY_HEIGHT, to + BODY_HEIGHT, { minX: x - r, minZ: z - r, maxX: x + r, maxZ: z + r }), x, z, r - HEAD_CONTACT);
  // 4–5. horizontal in steps, each followed by the ground, so a ramp or a stair lifts the feet before the next step meets it
  const sx = input.dx / steps, sz = input.dz / steps;
  for (let i = 0; i < steps; i++) {
    const from = { x, y, z, vy, grounded };
    const box = { minX: Math.min(x, x + sx) - r, minZ: Math.min(z, z + sz) - r, maxX: Math.max(x, x + sx) + r, maxZ: Math.max(z, z + sz) + r };
    // the legs below the hips, the body above them: a stair's next riser is inside the band only when it is
    // within LEG_RADIUS, so treads deeper than that are climbed by the ground under the centre alone (spec 2m-2 §3)
    ({ x, z } = moveBody(x, z, sx, sz, [
      { r: LEG_RADIUS, polys: slabPolygons(tris, y + STEP_UP + BAND_GAP, y + LEGS_TOP, box) },
      { r, polys: slabPolygons(tris, y + LEGS_TOP, y + BODY_HEIGHT, box) },
    ]));
    const inSlope = settle(from.y);
    // a step up, or (in the air) a surface above the feet that the body will land on: with no room for the head
    // there it would stand with a ceiling inside its band, and could not get out; and a centre inside a slope too
    // steep to walk would sink into it: either way the step is refused
    const stand = grounded ? y : groundUnder(tris, x, z, from.y, from.y + STEP_UP + BAND_GAP);
    if (inSlope || (stand !== null && roomless(from.y, stand))) ({ x, y, z, vy, grounded } = from);
  }
  // 6. never below the terrain
  const t = world.terrain(x, z);
  if (y < t) { y = t; vy = 0; grounded = true; }
  return { x, y, z, vy, grounded };
}
